import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, TrendingUp, Users, Activity, MapPin, DollarSign, Star, MessageSquare } from "lucide-react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import { api, formatApiError } from "../lib/ccsApi";
import { toast } from "sonner";

const COLORS = ["#1E3A8A", "#22C55E", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4"];

export default function AdminDashboard() {
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [perDay, setPerDay] = useState([]);
    const [modeUsage, setModeUsage] = useState([]);
    const [income, setIncome] = useState([]);
    const [states, setStates] = useState([]);
    const [types, setTypes] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [recentComments, setRecentComments] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                await api.get("/auth/me");
                const [ov, pd, mu, inc, st, ty, fb, fc] = await Promise.all([
                    api.get("/admin/stats/overview"),
                    api.get("/admin/stats/usage-per-day"),
                    api.get("/admin/stats/mode-usage"),
                    api.get("/admin/stats/income-distribution"),
                    api.get("/admin/stats/state-usage"),
                    api.get("/admin/stats/childcare-type"),
                    api.get("/admin/feedback/overview"),
                    api.get("/admin/feedback/recent"),
                ]);
                setOverview(ov.data);
                setPerDay(pd.data);
                setModeUsage(mu.data);
                setIncome(inc.data);
                setStates(st.data);
                setTypes(ty.data);
                setFeedback(fb.data);
                setRecentComments(fc.data);
            } catch (err) {
                if (err?.response?.status === 401) {
                    nav("/admin/login");
                    return;
                }
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [nav]);

    async function logout() {
        await api.post("/auth/logout").catch(() => {});
        localStorage.removeItem("ccs_admin_token");
        nav("/admin/login");
    }

    if (loading) {
        return <div className="max-w-7xl mx-auto px-4 py-16 text-slate-500" data-testid="admin-loading">Loading…</div>;
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Admin</span>
                    <h1 className="font-heading font-bold text-3xl sm:text-4xl text-[#0F172A] mt-2">Dashboard</h1>
                    <p className="text-slate-600 text-sm mt-1">Anonymous usage analytics. No personally identifiable data.</p>
                </div>
                <Button variant="outline" onClick={logout} className="rounded-full self-start sm:self-auto" data-testid="admin-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                </Button>
            </div>

            {/* KPI cards */}
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard icon={<Activity />} label="Total calculations" value={overview?.total_calculations ?? 0} testid="kpi-total-calcs" />
                <KpiCard icon={<Users />} label="Email opt-ins" value={overview?.total_email_optins ?? 0} testid="kpi-optins" />
                <KpiCard icon={<Star />} label="Avg rating" value={feedback?.average_rating ?? "—"} testid="kpi-avg-rating" />
                <KpiCard icon={<MessageSquare />} label="Feedback received" value={feedback?.total_feedback ?? 0} testid="kpi-feedback-count" />
            </div>

            {/* Charts */}
            <div className="mt-8 grid lg:grid-cols-2 gap-6">
                <ChartCard title="Users per day" icon={<TrendingUp className="h-4 w-4" />} testid="chart-per-day">
                    {perDay.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={perDay}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="day" stroke="#64748B" fontSize={12} />
                                <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                                <Tooltip />
                                <Line type="monotone" dataKey="count" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <ChartCard title="Calculator mode usage" icon={<Activity className="h-4 w-4" />} testid="chart-mode-usage">
                    {modeUsage.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={modeUsage} dataKey="count" nameKey="mode" innerRadius={55} outerRadius={90} paddingAngle={2}>
                                    {modeUsage.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <ChartCard title="Income range distribution" icon={<DollarSign className="h-4 w-4" />} testid="chart-income">
                    {income.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={income}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="bucket" stroke="#64748B" fontSize={12} />
                                <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="count" fill="#22C55E" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <ChartCard title="State usage" icon={<MapPin className="h-4 w-4" />} testid="chart-state">
                    {states.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={states}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="state" stroke="#64748B" fontSize={12} />
                                <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="count" fill="#1E3A8A" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>

            <div className="mt-6 grid lg:grid-cols-2 gap-6">
                <ChartCard title="Childcare type mix" icon={<Activity className="h-4 w-4" />} testid="chart-cct">
                    {types.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={types}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="type" stroke="#64748B" fontSize={11} />
                                <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="count" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <div className="ccs-card" data-testid="avg-inputs-card">
                    <h3 className="font-heading font-semibold text-lg text-[#0F172A]">Averages across calculations</h3>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <Avg label="Avg children" value={fmtN(overview?.averages?.avg_num_children)} />
                        <Avg label="Avg activity hrs / fortnight" value={fmtN(overview?.averages?.avg_activity)} />
                        <Avg label="Avg weekly parent" value={fmt(overview?.averages?.avg_weekly_parent)} />
                        <Avg label="Avg weekly subsidy" value={fmt(overview?.averages?.avg_weekly_subsidy)} />
                    </div>
                </div>
            </div>

            {/* Feedback section */}
            <div className="mt-6 grid lg:grid-cols-2 gap-6">
                <ChartCard title="Rating distribution" icon={<Star className="h-4 w-4" />} testid="chart-feedback">
                    {!feedback || (feedback.by_rating || []).length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={feedback.by_rating}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="rating" stroke="#64748B" fontSize={12} />
                                <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="count" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <div className="ccs-card" data-testid="feedback-recent">
                    <div className="flex items-center gap-2 text-slate-600 text-sm">
                        <MessageSquare className="h-4 w-4" />
                        <span className="uppercase tracking-[0.2em] text-xs">Recent comments</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-[#0F172A] mt-1">What parents are saying</h3>
                    <div className="mt-3 max-h-[240px] overflow-y-auto space-y-2 pr-1">
                        {(recentComments || []).length === 0 && (
                            <div className="text-sm text-slate-400">No comments yet.</div>
                        )}
                        {(recentComments || []).map((c) => (
                            <div key={c.id} className="bg-white rounded-lg border border-[#E2E8F0] p-3">
                                <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} className={`h-3.5 w-3.5 ${n <= c.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-slate-300"}`} />
                                    ))}
                                    <span className="ml-2 text-xs text-slate-400">{(c.day || c.created_at || "").slice(0, 10)}</span>
                                </div>
                                <p className="mt-1.5 text-sm text-slate-700">{c.comment}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function KpiCard({ icon, label, value, testid }) {
    return (
        <div className="ccs-card" data-testid={testid}>
            <div className="h-9 w-9 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] flex items-center justify-center">{icon}</div>
            <div className="mt-4 text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className="mt-1 font-heading font-bold text-2xl text-[#0F172A]">{value}</div>
        </div>
    );
}

function ChartCard({ title, icon, children, testid }) {
    return (
        <div className="ccs-card" data-testid={testid}>
            <div className="flex items-center gap-2 text-slate-600 text-sm">{icon}<span className="uppercase tracking-[0.2em] text-xs">{title}</span></div>
            <h3 className="font-heading font-semibold text-lg text-[#0F172A] mt-1">{title}</h3>
            <div className="mt-3 h-64 w-full">{children}</div>
        </div>
    );
}

function Empty() {
    return <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">No data yet</div>;
}

function Avg({ label, value }) {
    return (
        <div className="bg-white rounded-lg border border-[#E2E8F0] p-3">
            <div className="text-xs text-slate-500 uppercase tracking-[0.15em]">{label}</div>
            <div className="mt-1 font-heading font-semibold text-[#0F172A]">{value}</div>
        </div>
    );
}

function fmt(n) {
    if (n == null || isNaN(n)) return "—";
    return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(Number(n));
}
function fmtN(n) {
    if (n == null || isNaN(n)) return "—";
    return Number(n).toFixed(1);
}
